// App entry point
import React, { Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "next-themes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AccessibilityProvider } from "./contexts/AccessibilityContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import TherapistProtectedRoute from "./components/TherapistProtectedRoute";
import Layout from "./components/Layout";
import AppLayout from "./components/AppLayout";
import { InactivityMonitor } from "./components/InactivityMonitor";
import { PWAUpdatePrompt } from "./components/PWAUpdatePrompt";
import { UpdateAvailableDialog } from "./components/UpdateAvailableDialog";
import { MobileAppBanner } from "./components/MobileAppBanner";
import ScrollRestoration from "./components/ScrollRestoration";
import NotificationEngagementTracker from "./components/NotificationEngagementTracker";
import { useAppUpdatePrompt } from "./hooks/useAppUpdatePrompt";

import { NativeSplashGate } from "./components/NativeSplashGate";
import { PostLoginNotificationPrompt } from "./components/PostLoginNotificationPrompt";
import { useDeepLinks } from "./hooks/useDeepLinks";
import { useNativePlatform } from "./hooks/useNativePlatform";
import { usePageView } from "./hooks/usePageView";
import { lazyRetry } from "./lib/lazyRetry";
import * as analytics from "./lib/analytics";
import { noteSessionStart, touchLastVisit } from "./lib/analyticsContext";
import { ConsentBanner } from "./components/ConsentBanner";

import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";

// Critical routes - loaded immediately
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Welcome from "./pages/Welcome";

// Renders <Welcome /> on native (Capacitor iOS/Android) and <Index /> on web.
// Fires on every hit to `/`, including deep links.
const HomeGate: React.FC = () => (useNativePlatform() ? <Welcome /> : <Index />);

// Lazy-loaded routes with stale-chunk retry
const Home = lazyRetry(() => import("./pages/Home"));
const Discovery = lazyRetry(() => import("./pages/Discovery"));
const Messages = lazyRetry(() => import("./pages/Messages"));
const Profile = lazyRetry(() => import("./pages/Profile"));
const Settings = lazyRetry(() => import("./pages/Settings"));
const AccountNotices = lazyRetry(() => import("./pages/AccountNotices"));
const SupportResources = lazyRetry(() => import("./pages/SupportResources"));
const WearTheMission = lazyRetry(() => import("./pages/WearTheMission"));
const MarketingPreferences = lazyRetry(() => import("./pages/MarketingPreferences"));
const OrganizationSettings = lazyRetry(() => import("./pages/OrganizationSettings"));
const AdminOrganizations = lazyRetry(() => import("./pages/AdminOrganizations"));
const AdminOrganizationDetail = lazyRetry(() => import("./pages/AdminOrganizationDetail"));
const PartnerPortal = lazyRetry(() => import("./pages/PartnerPortal"));
const PartnerCampaign = lazyRetry(() => import("./pages/PartnerCampaign"));
const PartnerAccept = lazyRetry(() => import("./pages/PartnerPortal").then((m) => ({ default: m.PartnerAccept })));
const PartnerReset = lazyRetry(() => import("./pages/PartnerPortal").then((m) => ({ default: m.PartnerReset })));
const MarketingConfirm = lazyRetry(() => import("./pages/MarketingConfirm"));
const UserProfile = lazyRetry(() => import("./pages/UserProfile"));
const UserVents = lazyRetry(() => import("./pages/UserVents"));
const Bookmarks = lazyRetry(() => import("./pages/Bookmarks"));
const Moderation = lazyRetry(() => import("./pages/Moderation"));
const Room = lazyRetry(() => import("./pages/Room"));
const SingleVent = lazyRetry(() => import("./pages/SingleVent"));
const PrivacyPolicy = lazyRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyRetry(() => import("./pages/TermsOfService"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const Partnerships = lazyRetry(() => import("./pages/Partnerships"));
const TherapistSignup = lazyRetry(() => import("./pages/TherapistSignup"));
const TherapistAuth = lazyRetry(() => import("./pages/TherapistAuth"));
const TherapistDashboard = lazyRetry(() => import("./pages/TherapistDashboard"));
const TherapistProfile = lazyRetry(() => import("./pages/TherapistProfile"));
const TherapistAnalytics = lazyRetry(() => import("./pages/TherapistAnalytics"));
const TherapistDirectory = lazyRetry(() => import("./pages/TherapistDirectory"));
const TherapistPublicProfile = lazyRetry(() => import("./pages/TherapistPublicProfile"));
const TherapistPaymentSuccess = lazyRetry(() => import("./pages/TherapistPaymentSuccess"));
const TherapistSubscriptionRequired = lazyRetry(() => import("./pages/TherapistSubscriptionRequired"));
const Support = lazyRetry(() => import("./pages/Support"));
const SupportDashboard = lazyRetry(() => import("./pages/SupportDashboard"));
const KnowledgeBase = lazyRetry(() => import("./pages/KnowledgeBase"));
const Analytics = lazyRetry(() => import("./pages/Analytics"));
const ProviderAgreement = lazyRetry(() => import("./pages/ProviderAgreement"));
const SubscriptionTerms = lazyRetry(() => import("./pages/SubscriptionTerms"));
const DMCA = lazyRetry(() => import("./pages/DMCA"));

const Feedback = lazyRetry(() => import("./pages/Feedback"));
const FeedbackDetail = lazyRetry(() => import("./pages/FeedbackDetail"));
const WellnessCalendar = lazyRetry(() => import("./pages/WellnessCalendar"));
const Notifications = lazyRetry(() => import("./pages/Notifications"));

const DashboardSelector = lazyRetry(() => import("./pages/DashboardSelector"));
const TherapistVerification = lazyRetry(() => import("./pages/TherapistVerification"));
const ArticlePage = lazyRetry(() => import("./pages/ArticlePage"));
const CreateArticle = lazyRetry(() => import("./components/articles/CreateArticle"));
const ManageArticles = lazyRetry(() => import("./components/articles/ManageArticles"));
const Onboarding = lazyRetry(() => import("./components/Onboarding"));
const VerifyEmail = lazyRetry(() => import("./pages/VerifyEmail"));
const Install = lazyRetry(() => import("./pages/Install"));
const About = lazyRetry(() => import("./pages/About"));
const FAQ = lazyRetry(() => import("./pages/FAQ"));
const Contact = lazyRetry(() => import("./pages/Contact"));
const AnonymousVentApp = lazyRetry(() => import("./pages/AnonymousVentApp"));
const Resources = lazyRetry(() => import("./pages/Resources"));
const ResourceArticle = lazyRetry(() => import("./pages/ResourceArticle"));

// Admin components
const AdminLayout = lazyRetry(() => import("./components/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminDashboard = lazyRetry(() => import("./components/admin/AdminDashboard").then(m => ({ default: m.AdminDashboard })));
const ErrorFeed = lazyRetry(() => import("./components/admin/ErrorFeed").then(m => ({ default: m.ErrorFeed })));
const BackgroundJobsMonitor = lazyRetry(() => import("./components/admin/BackgroundJobsMonitor").then(m => ({ default: m.BackgroundJobsMonitor })));
const ContentHoldsQueue = lazyRetry(() => import("./components/admin/ContentHoldsQueue").then(m => ({ default: m.ContentHoldsQueue })));
const AdminAuditLog = lazyRetry(() => import("./pages/AdminAuditLog"));
const AdminRetentionInbox = lazyRetry(() => import("./pages/AdminRetentionInbox"));
const AdminDailyAffirmations = lazyRetry(() => import("./pages/AdminDailyAffirmations"));
const AdminNotificationCopy = lazyRetry(() => import("./pages/AdminNotificationCopy"));
const AdminContactMessages = lazyRetry(() => import("./pages/AdminContactMessages"));
const AdminQuestionOfTheDay = lazyRetry(() => import("./pages/AdminQuestionOfTheDay"));


const queryClient = new QueryClient();

// Suspense fallback component - returns null so splash gate stays visible
const PageLoader = () => null;

// Initialize native keyboard behavior
if (Capacitor.isNativePlatform()) {
  Keyboard.setAccessoryBarVisible({ isVisible: true }).catch(() => {});
  Keyboard.setScroll({ isDisabled: false }).catch(() => {});
}

// Inner component that handles routing and splash gate readiness
const AppContent = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showPrompt, required: updateRequired, config: updateConfig, dismiss: dismissUpdate } = useAppUpdatePrompt();
  useDeepLinks();

  usePageView();

  useEffect(() => {
    const platform = (Capacitor.getPlatform() === 'ios'
      ? 'ios'
      : Capacitor.getPlatform() === 'android'
        ? 'android'
        : 'web') as 'web' | 'ios' | 'android';
    analytics.track('App Opened', {
      platform,
      app_version: __APP_VERSION__,
    });
    const returned = noteSessionStart();
    if (returned) analytics.track('session_returned', { ...returned, platform });
    const touch = () => touchLastVisit();
    window.addEventListener('pagehide', touch);
    return () => window.removeEventListener('pagehide', touch);
  }, []);

  // Handle redirect for logged-in users at root
  useEffect(() => {
    if (!loading && user && location.pathname === '/') {
      const recoveryPending = sessionStorage.getItem('password_recovery_pending');
      if (!recoveryPending) {
        navigate('/app', { replace: true });
      }
    }
  }, [loading, user, location.pathname, navigate]);

  // Check for deletion pending flag (set by AuthContext when user has pending deletion)
  useEffect(() => {
    if (!loading && sessionStorage.getItem('deletion_pending') === 'true') {
      sessionStorage.removeItem('deletion_pending');
      navigate('/?deletion_pending=true', { replace: true });
    }
  }, [loading, navigate]);

  // isReady = auth is resolved (loading is false)
  // On native: Capacitor SplashScreen plugin handles the 2-second splash
  // On web: NativeSplashGate shows React overlay for 2 seconds minimum
  const isReady = !loading;

  return (
    <NativeSplashGate isReady={isReady}>
      <ScrollRestoration />
      <NotificationEngagementTracker />
      <MobileAppBanner />
      <UpdateAvailableDialog open={showPrompt} required={updateRequired} config={updateConfig} onDismiss={dismissUpdate} />
      <PostLoginNotificationPrompt />
      <ConsentBanner />
      <Suspense fallback={<PageLoader />}>

        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<HomeGate />} />
            <Route path="auth" element={<Auth />} />
            
            <Route path="therapist-auth" element={<TherapistAuth />} />
            <Route path="therapist-signup" element={<ErrorBoundary><TherapistSignup /></ErrorBoundary>} />
            <Route path="therapist-subscription-required" element={<ErrorBoundary><TherapistSubscriptionRequired /></ErrorBoundary>} />
            <Route path="therapist-dashboard" element={
              <TherapistProtectedRoute>
                <ErrorBoundary><TherapistDashboard /></ErrorBoundary>
              </TherapistProtectedRoute>
            } />
            <Route path="therapist-profile" element={
              <TherapistProtectedRoute>
                <ErrorBoundary><TherapistProfile /></ErrorBoundary>
              </TherapistProtectedRoute>
            } />
            <Route path="therapist-analytics" element={
              <TherapistProtectedRoute>
                <ErrorBoundary><TherapistAnalytics /></ErrorBoundary>
              </TherapistProtectedRoute>
            } />
            <Route path="therapist-dashboard/articles" element={
              <TherapistProtectedRoute>
                <ErrorBoundary><ManageArticles /></ErrorBoundary>
              </TherapistProtectedRoute>
            } />
            <Route path="therapist-dashboard/articles/new" element={
              <TherapistProtectedRoute>
                <ErrorBoundary><CreateArticle /></ErrorBoundary>
              </TherapistProtectedRoute>
            } />
            <Route path="therapist-dashboard/articles/:articleId/edit" element={
              <TherapistProtectedRoute>
                <ErrorBoundary><CreateArticle /></ErrorBoundary>
              </TherapistProtectedRoute>
            } />
            
          </Route>
          <Route path="/partner" element={<ErrorBoundary><PartnerPortal /></ErrorBoundary>} />
          <Route path="/partner/campaign/:id" element={<ErrorBoundary><PartnerCampaign /></ErrorBoundary>} />
          <Route path="/partner/reset" element={<ErrorBoundary><PartnerReset /></ErrorBoundary>} />
          <Route path="/partner/accept" element={<ErrorBoundary><PartnerAccept /></ErrorBoundary>} />
          <Route path="/partner/:orgId" element={<ErrorBoundary><PartnerPortal /></ErrorBoundary>} />
          <Route path="/marketing/confirm" element={<ErrorBoundary><MarketingConfirm /></ErrorBoundary>} />
          <Route path="/dashboard-selector" element={
            <ProtectedRoute>
              <ErrorBoundary><DashboardSelector /></ErrorBoundary>
            </ProtectedRoute>
          } />
          <Route path="/app" element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route index element={<ErrorBoundary><Home /></ErrorBoundary>} />
            <Route path="discovery" element={<ErrorBoundary><Discovery /></ErrorBoundary>} />
            <Route path="messages" element={<ErrorBoundary><Messages /></ErrorBoundary>} />
            <Route path="profile" element={<ErrorBoundary><Profile /></ErrorBoundary>} />
            <Route path="settings" element={<ErrorBoundary><Settings /></ErrorBoundary>} />
            <Route path="notices" element={<ErrorBoundary><AccountNotices /></ErrorBoundary>} />
            <Route path="settings/marketing" element={<ErrorBoundary><MarketingPreferences /></ErrorBoundary>} />
            <Route path="settings/organization" element={<ErrorBoundary><OrganizationSettings /></ErrorBoundary>} />
            <Route path="wear-the-mission" element={<ErrorBoundary><WearTheMission /></ErrorBoundary>} />
            <Route path="user/:userId" element={<ErrorBoundary><UserProfile /></ErrorBoundary>} />
            <Route path="user/:userId/vents" element={<ErrorBoundary><UserVents /></ErrorBoundary>} />
            <Route path="my-vents" element={<ErrorBoundary><UserVents /></ErrorBoundary>} />
            <Route path="bookmarks" element={<ErrorBoundary><Bookmarks /></ErrorBoundary>} />
            <Route path="rooms/:roomId" element={<ErrorBoundary><Room /></ErrorBoundary>} />
            <Route path="vent/:ventId" element={<ErrorBoundary><SingleVent /></ErrorBoundary>} />
            <Route path="therapists" element={<ErrorBoundary><TherapistDirectory /></ErrorBoundary>} />
            <Route path="therapist/:userId" element={<ErrorBoundary><TherapistPublicProfile /></ErrorBoundary>} />
            <Route path="articles/:articleId" element={<ErrorBoundary><ArticlePage /></ErrorBoundary>} />
            <Route path="support" element={<ErrorBoundary><Support /></ErrorBoundary>} />
            <Route path="therapist/payment-success" element={<ErrorBoundary><TherapistPaymentSuccess /></ErrorBoundary>} />
            <Route path="feedback" element={<ErrorBoundary><Feedback /></ErrorBoundary>} />
            <Route path="feedback/:feedbackId" element={<ErrorBoundary><FeedbackDetail /></ErrorBoundary>} />
            <Route path="wellness/calendar" element={<ErrorBoundary><WellnessCalendar /></ErrorBoundary>} />
            <Route path="wellness/progress" element={<Navigate to="/app?tool=progress" replace />} />
            <Route path="notifications" element={<ErrorBoundary><Notifications /></ErrorBoundary>} />
          </Route>
          {/* Admin Panel with Sidebar */}
          <Route path="/app/admin" element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index element={<ErrorBoundary><AdminDashboard /></ErrorBoundary>} />
            <Route path="moderation" element={<ErrorBoundary><Moderation /></ErrorBoundary>} />
            <Route path="support" element={<ErrorBoundary><SupportDashboard /></ErrorBoundary>} />
            <Route path="therapist-verification" element={<ErrorBoundary><TherapistVerification /></ErrorBoundary>} />
            <Route path="knowledge-base" element={<ErrorBoundary><KnowledgeBase /></ErrorBoundary>} />
            <Route path="analytics" element={<ErrorBoundary><Analytics /></ErrorBoundary>} />
            <Route path="errors" element={<ErrorBoundary><ErrorFeed /></ErrorBoundary>} />
            <Route path="jobs" element={<ErrorBoundary><BackgroundJobsMonitor /></ErrorBoundary>} />
            <Route path="holds" element={<ErrorBoundary><ContentHoldsQueue /></ErrorBoundary>} />
            <Route path="audit-log" element={<ErrorBoundary><AdminAuditLog /></ErrorBoundary>} />
            <Route path="notifications" element={<ErrorBoundary><AdminRetentionInbox /></ErrorBoundary>} />
            <Route path="affirmations" element={<ErrorBoundary><AdminDailyAffirmations /></ErrorBoundary>} />
            <Route path="notification-copy" element={<ErrorBoundary><AdminNotificationCopy /></ErrorBoundary>} />
            <Route path="contact-messages" element={<ErrorBoundary><AdminContactMessages /></ErrorBoundary>} />
            <Route path="qotd" element={<ErrorBoundary><AdminQuestionOfTheDay /></ErrorBoundary>} />
            <Route path="organizations" element={<ErrorBoundary><AdminOrganizations /></ErrorBoundary>} />
            <Route path="organizations/:id" element={<ErrorBoundary><AdminOrganizationDetail /></ErrorBoundary>} />
          </Route>
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/install" element={<Install />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          {/* Redirect therapist-specific routes to unified pages with tab param */}
          <Route path="/therapist-privacy" element={<Navigate to="/privacy?tab=therapist" replace />} />
          <Route path="/therapist-terms" element={<Navigate to="/terms?tab=therapist" replace />} />
          <Route path="/therapist-faq" element={<Navigate to="/faq?tab=therapist" replace />} />
          <Route path="/provider-agreement" element={<ProviderAgreement />} />
          <Route path="/subscription-terms" element={<SubscriptionTerms />} />
          <Route path="/dmca" element={<DMCA />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/support" element={<SupportResources />} />
          <Route path="/account/notices" element={<ProtectedRoute><ErrorBoundary><AccountNotices standalone /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/partnerships" element={<Partnerships />} />
          <Route path="/anonymous-vent-app" element={<AnonymousVentApp />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resources/:slug" element={<ResourceArticle />} />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </NativeSplashGate>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <AuthProvider>
          <AccessibilityProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <InactivityMonitor />
              <PWAUpdatePrompt />
              <BrowserRouter>
                <AppContent />
              </BrowserRouter>
            </TooltipProvider>
          </AccessibilityProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
